import { ArrowLeft, ArrowUp } from "lucide-react";
import {
  constraintsFromHorizontalPin,
  constraintsFromVerticalPin,
  horizontalConstraintLabel,
  horizontalPinActive,
  verticalConstraintLabel,
  verticalPinActive,
  type HorizontalConstraint,
  type NodeConstraints,
  type VerticalConstraint,
} from "@/document/model/constraints";
import { frameAutoLayoutMode, type FrameNode } from "@/document/model/nodes";
import { PanelSelect, PanelSelectItem } from "./PanelSelect";
import { PanelSection } from "./PanelSection";

const horizontalOptions: HorizontalConstraint[] = ["left", "right", "leftRight", "center", "scale"];
const verticalOptions: VerticalConstraint[] = ["top", "bottom", "topBottom", "center", "scale"];

const FRAME_INSET = 10;
const CHILD_INSET = 26;

interface ConstraintsSectionProps {
  constraints: NodeConstraints | "mixed";
  parentFrame?: FrameNode;
  disabled?: boolean;
  onChange: (constraints: NodeConstraints) => void;
}

export function ConstraintsSection({
  constraints,
  parentFrame,
  disabled = false,
  onChange,
}: ConstraintsSectionProps) {
  if (!parentFrame || frameAutoLayoutMode(parentFrame) !== "off") {
    return null;
  }

  const resolved = constraints === "mixed" ? undefined : constraints;

  return (
    <PanelSection title="Constraints">
      <div className="flex gap-2">
        <div className="grid flex-1 gap-2">
          <ConstraintSelect
            value={constraints === "mixed" ? "mixed" : constraints.horizontal}
            icon={<ArrowLeft className="size-3.5" />}
            options={horizontalOptions}
            labelFor={horizontalConstraintLabel}
            disabled={disabled}
            onChange={horizontal => resolved && onChange({ ...resolved, horizontal })}
          />
          <ConstraintSelect
            value={constraints === "mixed" ? "mixed" : constraints.vertical}
            icon={<ArrowUp className="size-3.5" />}
            options={verticalOptions}
            labelFor={verticalConstraintLabel}
            disabled={disabled}
            onChange={vertical => resolved && onChange({ ...resolved, vertical })}
          />
        </div>

        <ConstraintVisualizer
          constraints={constraints}
          disabled={disabled}
          onPinHorizontal={side => resolved && onChange(constraintsFromHorizontalPin(side, resolved))}
          onPinVertical={side => resolved && onChange(constraintsFromVerticalPin(side, resolved))}
          onCenterHorizontal={() => resolved && onChange({
            ...resolved,
            horizontal: resolved.horizontal === "center" ? "left" : "center",
          })}
          onCenterVertical={() => resolved && onChange({
            ...resolved,
            vertical: resolved.vertical === "center" ? "top" : "center",
          })}
        />
      </div>
    </PanelSection>
  );
}

function ConstraintSelect<T extends string>({
  value,
  icon,
  options,
  labelFor,
  disabled,
  onChange,
}: {
  value: T | "mixed";
  icon: React.ReactNode;
  options: T[];
  labelFor: (value: T) => string;
  disabled?: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="grid size-7 shrink-0 place-items-center rounded bg-white/[0.04] text-slate-400">
        {icon}
      </span>
      <PanelSelect
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        triggerClassName="flex-1"
      >
        {options.map(option => (
          <PanelSelectItem key={option} value={option}>
            {labelFor(option)}
          </PanelSelectItem>
        ))}
      </PanelSelect>
    </div>
  );
}

function ConstraintVisualizer({
  constraints,
  disabled,
  onPinHorizontal,
  onPinVertical,
  onCenterHorizontal,
  onCenterVertical,
}: {
  constraints: NodeConstraints | "mixed";
  disabled?: boolean;
  onPinHorizontal: (side: "left" | "right") => void;
  onPinVertical: (side: "top" | "bottom") => void;
  onCenterHorizontal: () => void;
  onCenterVertical: () => void;
}) {
  const horizontal = constraints === "mixed" ? undefined : constraints.horizontal;
  const vertical = constraints === "mixed" ? undefined : constraints.vertical;
  const isDisabled = disabled || constraints === "mixed";

  return (
    <div className="relative size-[72px] shrink-0 rounded bg-white/[0.04]">
      <div
        className="pointer-events-none absolute rounded-[3px] border border-slate-500/70"
        style={{ inset: FRAME_INSET }}
      />
      <div
        className="pointer-events-none absolute rounded-[2px] border border-slate-400/70 bg-white/[0.06]"
        style={{ inset: CHILD_INSET }}
      />

      <ConstraintEdge
        edge="top"
        active={vertical ? verticalPinActive(vertical, "top") : false}
        disabled={isDisabled}
        onClick={() => onPinVertical("top")}
      />
      <ConstraintEdge
        edge="bottom"
        active={vertical ? verticalPinActive(vertical, "bottom") : false}
        disabled={isDisabled}
        onClick={() => onPinVertical("bottom")}
      />
      <ConstraintEdge
        edge="left"
        active={horizontal ? horizontalPinActive(horizontal, "left") : false}
        disabled={isDisabled}
        onClick={() => onPinHorizontal("left")}
      />
      <ConstraintEdge
        edge="right"
        active={horizontal ? horizontalPinActive(horizontal, "right") : false}
        disabled={isDisabled}
        onClick={() => onPinHorizontal("right")}
      />

      <button
        type="button"
        disabled={isDisabled}
        className="absolute left-1/2 top-1/2 z-10 flex h-4 w-7 -translate-x-1/2 -translate-y-1/2 cursor-default items-center justify-center disabled:cursor-not-allowed"
        onClick={onCenterHorizontal}
        aria-label="Center horizontally"
      >
        <span className={`h-px w-full rounded-full ${horizontal === "center" ? "bg-sky-400" : "bg-slate-500 hover:bg-slate-400"}`} />
      </button>
      <button
        type="button"
        disabled={isDisabled}
        className="absolute left-1/2 top-1/2 z-10 flex h-7 w-4 -translate-x-1/2 -translate-y-1/2 cursor-default items-center justify-center disabled:cursor-not-allowed"
        onClick={onCenterVertical}
        aria-label="Center vertically"
      >
        <span className={`h-full w-px rounded-full ${vertical === "center" ? "bg-sky-400" : "bg-slate-500 hover:bg-slate-400"}`} />
      </button>
    </div>
  );
}

function ConstraintEdge({
  edge,
  active,
  disabled,
  onClick,
}: {
  edge: "top" | "bottom" | "left" | "right";
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const lineColor = active ? "bg-sky-400" : "bg-slate-600 hover:bg-slate-500";
  const frameStart = FRAME_INSET;
  const frameEnd = 72 - FRAME_INSET;

  if (edge === "top" || edge === "bottom") {
    const y = edge === "top" ? frameStart : frameEnd;
  return (
      <button
        type="button"
        disabled={disabled}
        className="absolute left-0 right-0 flex cursor-default items-center justify-center disabled:cursor-not-allowed"
        style={{
          top: edge === "top" ? y - 7 : undefined,
          bottom: edge === "bottom" ? 72 - y - 7 : undefined,
          height: 14,
          paddingLeft: frameStart,
          paddingRight: frameStart,
        }}
        onClick={onClick}
        aria-label={`${edge} constraint`}
      >
        <span className={`h-px w-full rounded-full ${lineColor}`} />
      </button>
    );
  }

  const x = edge === "left" ? frameStart : frameEnd;
  return (
    <button
      type="button"
      disabled={disabled}
      className="absolute top-0 bottom-0 flex cursor-default items-center justify-center disabled:cursor-not-allowed"
      style={{
        left: edge === "left" ? x - 7 : undefined,
        right: edge === "right" ? 72 - x - 7 : undefined,
        width: 14,
        paddingTop: frameStart,
        paddingBottom: frameStart,
      }}
      onClick={onClick}
      aria-label={`${edge} constraint`}
    >
      <span className={`h-full w-px rounded-full ${lineColor}`} />
    </button>
  );
}
